import { useState } from 'react';
import { isBrowser } from '@utils/environment';
import { noop } from '@utils/event';
/**
 * Session Storage Interaction
 *
 * Using a hook, so we can also get at the value if it changes.
 *
 * Goal is to store the users "next survey", so we don't have to carry it in the url
 *
 *   1. Store "next survey: surveyNext" in sessionStorage
 *      a. SET on survey enter
 *      b. REMOVE on survey complete (if the survey completing is the same as the one stored)
 *   2. Remove "next survey: surveyNext" in sessionStorage by key.
 *
 *   Storage Keys;
 *      nextSurvey: string
 *
 *   @remark
 *   This only is good for the tab session of the browser. If the user closes the tab, the session is lost.
 */
/* Only usage so far, so we'll const it out here */
const KeyNextSurvey = 'nextSurvey';
const KeySessionIdentifier = '_ht_session_';

export const SESSION_STORAGE_TYPES = {
  localStorage: 'localStorage',
  sessionStorage: 'sessionStorage',
} as const;

const useSessionStorage = (storageType: keyof typeof SESSION_STORAGE_TYPES = 'localStorage') => {
  /* Local State */
  const [sessionKey, setSessionKey] = useState<string | string[]>('');

  if (!isBrowser()) {
    return {
      setSessionStorage: noop,
      removeSurveySessionStorage: noop,
      getSessionStorage: noop,
      sessionKey: '',
    };
  }

  /* -------- Methods ---------- */
  /* Note: Allow for a custom key, just in case you have additional usages */
  type TSetSessionStorage = {
    key?: string;
    value: string | string[];
  };
  const setSessionStorage = ({ key = KeyNextSurvey, value = '' }: TSetSessionStorage) => {
    const k = `${KeySessionIdentifier}${key}`;
    (window[storageType] as Storage).setItem(k, JSON.stringify(value));
    setSessionKey(value);
  };

  const removeSessionStorage = (key: string = KeyNextSurvey) => {
    const k = `${KeySessionIdentifier}${key}`;

    (window[storageType] as Storage).removeItem(k);
  };

  const getSessionStorage = (key: string = KeyNextSurvey) => {
    const k = `${KeySessionIdentifier}${key}`;
    const storage = Object.entries(window[storageType]);
    const keyRegExp = new RegExp(`${k}*`);
    const keyInStorage = storage.find(([sKey]) => keyRegExp.test(sKey)) || '';

    if (keyInStorage) {
      const [, value] = keyInStorage;
      return JSON.parse(value);
    }

    return '';
  };

  return {
    setSessionStorage,
    removeSessionStorage,
    getSessionStorage,
    sessionKey,
  };
};

export default useSessionStorage;
